import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useSignUpFlow } from '@mystudioclass/auth';
import { Video, Mail, Lock, Eye, EyeOff, User, AlertCircle } from '../components/Icons';
import toast from 'react-hot-toast';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export default function CustomerRegisterPage() {
  const { isLoaded, createSignUp, error: authError } = useSignUpFlow();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const studioIdFromUrl = searchParams.get('studioId');

  // Force light mode for public pages
  useEffect(() => {
    const previousTheme = document.documentElement.getAttribute('data-theme');
    document.documentElement.setAttribute('data-theme', 'light');
    return () => {
      if (previousTheme) {
        document.documentElement.setAttribute('data-theme', previousTheme);
      }
    };
  }, []);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [incompleteAccount, setIncompleteAccount] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isLoaded) {
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);
    setIncompleteAccount(false);

    try {
      // First, check if this email has an incomplete registration
      const checkResponse = await fetch(`${API_URL}/api/profile/check-incomplete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const checkResult = await checkResponse.json();

      if (checkResult.success && checkResult.data.exists) {
        // Account already exists
        if (checkResult.data.incomplete) {
          // Incomplete registration - prompt to sign in
          setIncompleteAccount(true);
          setLoading(false);
          return;
        } else {
          // Complete account exists
          toast.error('An account with this email already exists. Please sign in.');
          setLoading(false);
          return;
        }
      }

      // No existing account - proceed with signup
      const result = await createSignUp({
        emailAddress: email,
        password,
        firstName: '', // Will be collected in CompleteProfilePage
        lastName: '',  // Will be collected in CompleteProfilePage
        role: 'student' // Customer portal users are students, not owners
      });

      if (result?.data?.user) {
        // Store studioId for profile completion
        if (studioIdFromUrl) {
          sessionStorage.setItem('pendingStudioId', studioIdFromUrl);
        }
        toast.success('Account created! Please complete your profile.');
        // Use hard redirect to ensure fresh auth state
        // Pass studioId in URL (sessionStorage is backup)
        window.location.href = studioIdFromUrl
          ? `/complete-profile?studioId=${studioIdFromUrl}`
          : '/complete-profile';
      } else if (result?.error) {
        console.error('Sign up error:', result.error);
        // Check if error is about existing email
        const errorMsg = result.error.message || '';
        if (errorMsg.toLowerCase().includes('exist') || errorMsg.toLowerCase().includes('duplicate')) {
          setIncompleteAccount(true);
        } else {
          toast.error(errorMsg || 'Registration failed. Please try again.');
        }
      }
    } catch (error) {
      console.error('Sign-up error:', error);
      const errorMessage = error.message || authError?.message || 'Registration failed. Please try again.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleSignInToComplete = () => {
    // Redirect to login with return URL to complete-profile
    const loginUrl = studioIdFromUrl
      ? `/login?returnTo=/complete-profile?studioId=${studioIdFromUrl}`
      : '/login?returnTo=/complete-profile';
    navigate(loginUrl);
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-12"
      style={{ background: 'var(--bg-base)' }}
    >
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-xl mb-4"
            style={{
              background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
              boxShadow: '0 0 40px var(--primary-30)'
            }}
          >
            <Video className="w-8 h-8" style={{ color: 'var(--bg-base)' }} />
          </div>
          <h1 className="text-3xl font-bold" style={{ color: 'var(--text-primary)' }}>My Studio Class</h1>
          <p style={{ color: 'var(--text-muted)' }} className="mt-2">Customer Portal</p>
        </div>

        {/* Registration Form - Step 1: Email & Password */}
        <div
          className="rounded-2xl shadow-xl p-8"
          style={{
            background: 'var(--bg-surface)',
            border: '1px solid var(--border-muted)'
          }}
        >
          <div
            className="flex items-center justify-center mb-6 p-3 rounded-lg"
            style={{ background: 'var(--bg-muted)' }}
          >
            <User className="w-5 h-5 mr-2" style={{ color: 'var(--primary)' }} />
            <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>Customer Sign Up</span>
          </div>

          {/* Incomplete Account Message */}
          {incompleteAccount && (
            <div
              className="mb-6 p-4 rounded-lg"
              style={{
                background: 'var(--warning-10, rgba(245, 158, 11, 0.1))',
                border: '1px solid var(--warning, #f59e0b)'
              }}
            >
              <div className="flex items-start">
                <AlertCircle className="w-5 h-5 mr-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--warning, #f59e0b)' }} />
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                    Registration already started
                  </p>
                  <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                    An account with this email was created but not completed. Please sign in to continue your registration.
                  </p>
                  <button
                    type="button"
                    onClick={handleSignInToComplete}
                    className="mt-3 text-sm font-medium underline"
                    style={{ color: 'var(--primary)' }}
                  >
                    Sign in to continue
                  </button>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="label">
                <Mail className="w-4 h-4 inline mr-1" />
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="input"
                placeholder="you@example.com"
                required
                disabled={!isLoaded || loading}
              />
            </div>

            <div>
              <label className="label">
                <Lock className="w-4 h-4 inline mr-1" />
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input pr-10"
                  placeholder="At least 8 characters"
                  minLength={8}
                  required
                  disabled={!isLoaded || loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors"
                  style={{ color: 'var(--text-muted)' }}
                  onMouseOver={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
                  onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-muted)'}
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={!isLoaded || loading}
              className="btn btn-primary w-full"
            >
              {loading ? 'Creating Account...' : 'Continue'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-medium transition-colors"
                style={{ color: 'var(--primary)' }}
                onMouseOver={(e) => e.currentTarget.style.color = 'var(--primary-hover)'}
                onMouseOut={(e) => e.currentTarget.style.color = 'var(--primary)'}
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
